import type { ComponentType, SVGProps } from 'react'
import toolIconSprite from '@/assets/tool-icons.svg?url'
import { cn } from '@/lib/utils'
import {
  brandColorOnDark,
  distinctIcons,
  iconKey,
  toolIcon,
  toolIcons,
  type BrandIcon,
  type GenericIconName,
  type ToolIcon,
} from '@/lib/toolIcons'
import {
  IconCard,
  IconCloud,
  IconCode,
  IconDatabase,
  IconGlobe,
  IconImage,
  IconKeyboard,
  IconLanguage,
  IconLayers,
  IconMonitor,
  IconPen,
  IconPlug,
  IconSearch,
  IconSparkles,
  IconTrendingUp,
  IconVideo,
  IconWorkflow,
} from '@/components/Icons'

type GenericIcon = ComponentType<SVGProps<SVGSVGElement> & { size?: number }>

const GENERIC_ICONS: Record<GenericIconName, GenericIcon> = {
  sparkles: IconSparkles,
  plug: IconPlug,
  monitor: IconMonitor,
  language: IconLanguage,
  keyboard: IconKeyboard,
  globe: IconGlobe,
  search: IconSearch,
  chart: IconTrendingUp,
  layers: IconLayers,
  pen: IconPen,
  workflow: IconWorkflow,
  cloud: IconCloud,
  card: IconCard,
  database: IconDatabase,
  video: IconVideo,
  image: IconImage,
  code: IconCode,
}

export type LogoTone = 'brand' | 'mono'

export function BrandMark({
  icon,
  size = 16,
  tone = 'brand',
  className,
}: {
  icon: BrandIcon
  size?: number
  tone?: LogoTone
  className?: string
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      fill={tone === 'brand' ? brandColorOnDark(icon.hex) : 'currentColor'}
      className={cn('shrink-0', className)}
    >
      <use href={`${toolIconSprite}#${icon.slug}`} />
    </svg>
  )
}

export function ToolGlyph({
  icon,
  size = 16,
  tone = 'brand',
  className,
}: {
  icon: ToolIcon
  size?: number
  tone?: LogoTone
  className?: string
}) {
  if (icon.kind === 'brand') {
    return <BrandMark icon={icon.icon} size={size} tone={tone} className={className} />
  }
  const Generic = GENERIC_ICONS[icon.icon]
  return (
    <Generic
      size={size}
      aria-hidden="true"
      focusable="false"
      strokeWidth={1.8}
      className={cn(
        'shrink-0',
        tone === 'brand' && 'text-[var(--color-brand-accent-readable)]',
        className,
      )}
    />
  )
}

export function ToolLogo({
  name,
  size = 16,
  tone = 'brand',
  className,
}: {
  name: string
  size?: number
  tone?: LogoTone
  className?: string
}) {
  return <ToolGlyph icon={toolIcon(name)} size={size} tone={tone} className={className} />
}

export function ToolLogoStack({
  tool,
  size = 16,
  tone = 'brand',
  className,
}: {
  tool: string
  size?: number
  tone?: LogoTone
  className?: string
}) {
  const icons = distinctIcons(toolIcons(tool))
  return (
    <span aria-hidden="true" className={cn('inline-flex shrink-0 items-center gap-1', className)}>
      {icons.map((icon) => (
        <ToolGlyph key={iconKey(icon)} icon={icon} size={size} tone={tone} />
      ))}
    </span>
  )
}

export function ToolTag({ name, className }: { name: string; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border border-white/[0.07] bg-white/[0.03] py-0.5 pl-1.5 pr-2 font-code text-[11px] text-[var(--surface-muted)]',
        className,
      )}
    >
      <ToolLogo name={name} size={12} />
      {name}
    </span>
  )
}
